// context/AchievementContext.jsx
// Global Achievement Celebration State Management
// ============================================
// This context manages a queue of achievements to celebrate
// and provides methods to trigger celebrations from anywhere in the app

import React, { createContext, useContext, useState, useCallback } from 'react';

const AchievementContext = createContext(null);

export const useAchievements = () => {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievements must be used within an AchievementProvider');
  }
  return context;
};

export const AchievementProvider = ({ children }) => {
  // Queue of achievements waiting to be celebrated
  const [achievementQueue, setAchievementQueue] = useState([]);
  
  // Currently displaying achievement
  const [currentAchievement, setCurrentAchievement] = useState(null);
  
  // Is celebration modal open
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Add achievements to the celebration queue
   * Called after any action that might earn achievements
   * @param {Array} achievements - Array of achievement objects
   */
  const celebrateAchievements = useCallback((achievements) => {
    if (!achievements || achievements.length === 0) return;
    
    console.log('[ACHIEVEMENT] Adding to queue:', achievements.map(a => a.name));
    
    setAchievementQueue(prev => [...prev, ...achievements]);
    
    // If no celebration is currently showing, start one
    setCurrentAchievement(current => {
      if (!current) {
        const first = achievements[0];
        setIsOpen(true);
        // Remove first from queue since we're showing it
        setAchievementQueue(prev => prev.slice(1));
        return first;
      }
      return current;
    });
  }, []);

  /**
   * Dismiss current achievement and show next in queue
   */
  const dismissCurrent = useCallback(() => {
    setAchievementQueue(prev => {
      if (prev.length > 0) {
        // Show next achievement
        const [next, ...rest] = prev;
        setCurrentAchievement(next);
        return rest;
      } else {
        // No more achievements, close modal
        setIsOpen(false);
        setCurrentAchievement(null);
        return [];
      }
    });
  }, []);

  /**
   * Dismiss all achievements
   */
  const dismissAll = useCallback(() => {
    setAchievementQueue([]);
    setCurrentAchievement(null);
    setIsOpen(false);
  }, []);

  /**
   * Check API response for new achievements and celebrate them
   * Use this after any API call that might return achievements
   * @param {Object} response - API response object
   */
  const checkResponseForAchievements = useCallback((response) => {
    // Handle different response shapes
    const achievements = 
      response?.newAchievements || 
      response?.data?.newAchievements ||
      response?.newlyAchieved ||
      response?.data?.newlyAchieved ||
      null;
    
    if (achievements && achievements.length > 0) {
      celebrateAchievements(achievements);
      return true;
    }
    return false;
  }, [celebrateAchievements]);

  const value = {
    // State
    currentAchievement,
    achievementQueue,
    isOpen,
    queueLength: achievementQueue.length,
    
    // Actions
    celebrateAchievements,
    dismissCurrent,
    dismissAll,
    checkResponseForAchievements,
  };

  return (
    <AchievementContext.Provider value={value}>
      {children}
    </AchievementContext.Provider>
  );
};

export default AchievementContext;